import { useMemo } from 'react';

import { useGetDxfSceneMapping } from '@frontend/api';

import { useCanViewDxfMapping } from 'modules/common/viewer/dxfViewer/mapping/mappingAccess';
import { toAssignmentsMap } from 'modules/common/viewer/dxfViewer/session/mappingModel';
import type { MappingAssignments } from 'modules/common/viewer/dxfViewer/session/types';

/**
 * React Query boundary for the saved mapping. Maps the fetch into the
 * assignments shape the session loads — Viewer.tsx never sees DxfSceneMapping.
 *
 * `undefined` while the query has not resolved, so the caller leaves the session
 * alone until there is a real answer (including "no mapping row yet"). A user
 * who may not view mappings never fetches one, so it stays `undefined`, and the
 * canvas gets no role tint.
 */
export const useDxfSceneMapping = (
  partFileUuid: string | null | undefined,
): MappingAssignments | null | undefined => {
  const canViewMapping = useCanViewDxfMapping();
  const { data, isSuccess } = useGetDxfSceneMapping({
    partFileUuid: canViewMapping ? partFileUuid : null,
  });

  return useMemo(() => {
    if (!isSuccess) {
      return undefined;
    }
    if (data === null) {
      return null;
    }
    return toAssignmentsMap(data.assignments);
  }, [isSuccess, data]);
};
